import { MESSAGE_TYPE } from 'shared/constants/messages';
import { showBadgeOnFavicon } from './faviconHelper';
import { initFaviconSwitcher } from './faviconHelper';

import { EVENT_TYPES } from 'dashboard/routes/dashboard/settings/profile/constants.js';
import GlobalStore from 'dashboard/store';
import AudioNotificationStore from './AudioNotificationStore';
import {
  isConversationAssignedToMe,
  isConversationUnassigned,
  isMessageFromCurrentUser,
} from './AudioMessageHelper';
import WindowVisibilityHelper from './WindowVisibilityHelper';
import { useAlert } from 'dashboard/composables';
import {
  DEFAULT_NOTIFICATION_SOUND_ENABLED,
  DEFAULT_NOTIFICATION_SOUND_PREFERENCES,
  DEFAULT_REPEAT_NEW_MESSAGE_SOUND_UNTIL_READ,
  LEGACY_TONE_FILE_MAP,
  NOTIFICATION_SOUND_EVENTS,
  NOTIFICATION_SOUND_FILE_MAP,
  sanitizeNotificationSoundPreferences,
} from './constants';

const NOTIFICATION_TIME = 30000;
const ALERT_DURATION = 10000;
const ALERT_PATH_PREFIX = '/audio/dashboard/';
const DEFAULT_TONE = 'message';
const DEFAULT_ALERT_TYPE = ['none'];
const DEFAULT_ALERT_TYPE_STRING = DEFAULT_ALERT_TYPE.join('+');
const NOTIFICATION_TYPE_TO_SOUND_EVENT = Object.freeze({
  conversation_assignment: NOTIFICATION_SOUND_EVENTS.NEW_TICKETS_TO_HANDLE,
});

export class DashboardAudioNotificationHelper {
  constructor(store) {
    if (!store) {
      throw new Error('store is required');
    }
    this.store = new AudioNotificationStore(store);

    this.notificationConfig = {
      audioAlertType: DEFAULT_ALERT_TYPE,
      playAlertOnlyWhenHidden: true,
      alertIfUnreadConversationExist: false,
    };

    this.notificationSoundConfig = {
      enabled: DEFAULT_NOTIFICATION_SOUND_ENABLED,
      repeatNewMessageSoundUntilRead:
        DEFAULT_REPEAT_NEW_MESSAGE_SOUND_UNTIL_READ,
      preferences: { ...DEFAULT_NOTIFICATION_SOUND_PREFERENCES },
    };

    this.recurringNotificationTimer = null;

    this.audioConfig = {
      audio: null,
      tone: DEFAULT_TONE,
      hasSentSoundPermissionsRequest: false,
      eventAudios: {},
    };

    // Web Audio API state. Playing decoded buffers through a resumed
    // AudioContext is far more reliable than HTMLAudioElement.play(), which the
    // browser autoplay policy can silently block (the root cause of the
    // intermittent "sometimes no notification sound" reports).
    this.audioContext = null;
    this.audioBuffers = {};
    this.audioBufferPromises = {};
    this.audioUnlocked = false;
    this.unlockListenerAttached = false;

    this.currentUser = null;

    // Arm the one-time user-gesture "unlock" as early as possible so audio is
    // allowed to play by the time the first message arrives.
    this.setupAudioUnlock();
  }

  buildAudioResourceUrl = soundFileName => {
    return `${ALERT_PATH_PREFIX}${soundFileName}.mp3`;
  };

  resolveLegacyTone = tone => {
    return LEGACY_TONE_FILE_MAP[tone] || DEFAULT_TONE;
  };

  // ── Web Audio API (primary, reliable path) ──────────────────────────
  getAudioContext = () => {
    if (this.audioContext) {
      return this.audioContext;
    }
    if (typeof window === 'undefined') {
      return null;
    }
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) {
      return null;
    }
    try {
      this.audioContext = new AudioContextClass();
    } catch (error) {
      this.audioContext = null;
    }
    return this.audioContext;
  };

  // Decode a sound file into a reusable AudioBuffer. Decoding works even while
  // the context is suspended, so the buffer is ready the moment it resumes.
  loadAudioBuffer = fileName => {
    if (!fileName) {
      return Promise.resolve(null);
    }
    if (this.audioBuffers[fileName]) {
      return Promise.resolve(this.audioBuffers[fileName]);
    }
    if (this.audioBufferPromises[fileName]) {
      return this.audioBufferPromises[fileName];
    }
    const context = this.getAudioContext();
    if (!context) {
      return Promise.resolve(null);
    }

    const resourceUrl = this.buildAudioResourceUrl(fileName);
    const bufferPromise = fetch(resourceUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Unable to fetch audio: ${resourceUrl}`);
        }
        return response.arrayBuffer();
      })
      .then(data => context.decodeAudioData(data))
      .then(audioBuffer => {
        this.audioBuffers[fileName] = audioBuffer;
        delete this.audioBufferPromises[fileName];
        return audioBuffer;
      })
      .catch(() => {
        delete this.audioBufferPromises[fileName];
        return null;
      });

    this.audioBufferPromises[fileName] = bufferPromise;
    return bufferPromise;
  };

  // Play a decoded buffer through the AudioContext. Each call uses a fresh
  // source node, so overlapping / rapid notifications never interrupt each
  // other. Returns true only when playback was actually started.
  playViaWebAudio = fileName => {
    const context = this.audioContext;
    if (!context) {
      return false;
    }
    if (context.state !== 'running') {
      // Browser auto-suspended the context – try to recover (works while the
      // page still has user activation) and fall back to <audio> for now.
      if (context.state === 'suspended') {
        context.resume().catch(() => {});
      }
      return false;
    }
    const audioBuffer = this.audioBuffers[fileName];
    if (!audioBuffer) {
      // Not decoded yet – warm it up for next time and fall back for now.
      this.loadAudioBuffer(fileName);
      return false;
    }
    try {
      const source = context.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(context.destination);
      source.start(0);
      return true;
    } catch (error) {
      return false;
    }
  };

  // ── HTMLAudioElement (fallback path) ────────────────────────────────
  getFallbackAudio = fileName => {
    if (!fileName) {
      return null;
    }
    if (!this.audioConfig.eventAudios[fileName]) {
      const audio = new Audio(this.buildAudioResourceUrl(fileName));
      audio.preload = 'auto';
      try {
        audio.load();
      } catch (error) {
        // best-effort preload
      }
      this.audioConfig.eventAudios[fileName] = audio;
    }
    return this.audioConfig.eventAudios[fileName];
  };

  intializeAudio = () => {
    const toneFile = this.resolveLegacyTone(this.audioConfig.tone);
    this.audioConfig.audio = this.getFallbackAudio(toneFile);
    return this.audioConfig.audio;
  };

  getEventAudio = eventKey => {
    return this.getFallbackAudio(NOTIFICATION_SOUND_FILE_MAP[eventKey]);
  };

  handlePlaybackError = error => {
    if (!error || error.name !== 'NotAllowedError') {
      return;
    }
    // Autoplay was blocked – re-arm the gesture unlock so the next user
    // interaction restores sound, and surface the one-time permission hint.
    this.audioUnlocked = false;
    this.setupAudioUnlock();
    if (!this.audioConfig.hasSentSoundPermissionsRequest) {
      this.audioConfig.hasSentSoundPermissionsRequest = true;
      useAlert(
        'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.SOUND_PERMISSION_ERROR',
        { usei18n: true, duration: ALERT_DURATION }
      );
    }
  };

  playAudioElement = audio => {
    if (!audio) {
      return;
    }
    try {
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(this.handlePlaybackError);
      }
    } catch (error) {
      this.handlePlaybackError(error);
    }
  };

  triggerAudioPlayback = audio => {
    if (!audio) {
      return false;
    }
    // Play a fresh clone each time so rapid, overlapping notifications don't
    // interrupt one another (reusing a single element + resetting currentTime
    // throws AbortError and drops sounds).
    let node = audio;
    try {
      node = audio.cloneNode(true);
    } catch (error) {
      node = audio;
    }
    try {
      node.currentTime = 0;
    } catch (error) {
      // currentTime can throw before metadata loads – safe to ignore
    }
    this.playAudioElement(node);
    return true;
  };

  // Unified playback entry point: prefer Web Audio, gracefully fall back to
  // an <audio> element clone.
  playSoundFile = fileName => {
    if (!fileName) {
      return false;
    }
    if (this.playViaWebAudio(fileName)) {
      return true;
    }
    return this.triggerAudioPlayback(this.getFallbackAudio(fileName));
  };

  // ── Autoplay unlock (browsers require a user gesture) ────────────────
  setupAudioUnlock = () => {
    if (typeof window === 'undefined' || this.unlockListenerAttached) {
      return;
    }
    this.unlockListenerAttached = true;
    // Listeners stay attached so any later interaction also re-resumes the
    // context if the browser auto-suspends it; the work is guarded + cheap.
    ['pointerdown', 'keydown', 'touchstart', 'click'].forEach(eventName => {
      window.addEventListener(eventName, this.handleUnlockGesture, {
        capture: true,
        passive: true,
      });
    });
  };

  handleUnlockGesture = () => {
    const context = this.getAudioContext();
    if (context && context.state === 'suspended') {
      context.resume().catch(() => {});
    }
    if (this.audioUnlocked) {
      return;
    }
    this.audioUnlocked = true;
    // Prime the <audio> fallbacks (muted play/pause) so they're allowed later,
    // and decode buffers for the sounds this user has enabled.
    Object.values(this.audioConfig.eventAudios).forEach(this.primeAudioElement);
    this.warmUpSounds({ decode: true });
  };

  primeAudioElement = audio => {
    if (!audio) {
      return;
    }
    try {
      audio.muted = true;
      const playPromise = audio.play();
      const reset = () => {
        try {
          audio.pause();
          audio.currentTime = 0;
        } catch (error) {
          // ignore
        }
        audio.muted = false;
      };
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.then(reset).catch(() => {
          audio.muted = false;
        });
      } else {
        reset();
      }
    } catch (error) {
      audio.muted = false;
    }
  };

  // Preload the sounds the current user can trigger. With `decode: true` (once
  // the context exists) it also decodes them into AudioBuffers.
  warmUpSounds = ({ decode = false } = {}) => {
    const fileNames = new Set();
    fileNames.add(this.resolveLegacyTone(this.audioConfig.tone));
    Object.keys(NOTIFICATION_SOUND_FILE_MAP).forEach(eventKey => {
      if (this.isNotificationSoundEnabled(eventKey)) {
        fileNames.add(NOTIFICATION_SOUND_FILE_MAP[eventKey]);
      }
    });
    fileNames.forEach(fileName => {
      this.getFallbackAudio(fileName);
      if (decode) {
        this.loadAudioBuffer(fileName);
      }
    });
  };

  playAudioAlert = () => {
    return this.playSoundFile(this.resolveLegacyTone(this.audioConfig.tone));
  };

  isNotificationSoundEnabled = eventKey => {
    return (
      this.notificationSoundConfig.enabled &&
      !!this.notificationSoundConfig.preferences[eventKey]
    );
  };

  playNotificationSound = eventKey => {
    if (!this.isNotificationSoundEnabled(eventKey)) {
      return false;
    }
    return this.playSoundFile(NOTIFICATION_SOUND_FILE_MAP[eventKey]);
  };

  playTestSound = eventKey => {
    return this.playSoundFile(NOTIFICATION_SOUND_FILE_MAP[eventKey]);
  };

  shouldRepeatNewMessageSound = () => {
    return (
      this.notificationSoundConfig.repeatNewMessageSoundUntilRead &&
      this.isNotificationSoundEnabled(NOTIFICATION_SOUND_EVENTS.NEW_MESSAGE)
    );
  };

  set = ({
    currentUser,
    alwaysPlayAudioAlert,
    alertIfUnreadConversationExist,
    audioAlertType = DEFAULT_ALERT_TYPE,
    audioAlertTone = DEFAULT_TONE,
    notificationSoundEnabled = DEFAULT_NOTIFICATION_SOUND_ENABLED,
    repeatNewMessageSoundUntilRead = DEFAULT_REPEAT_NEW_MESSAGE_SOUND_UNTIL_READ,
    notificationSoundPreferences = DEFAULT_NOTIFICATION_SOUND_PREFERENCES,
  }) => {
    const serializedAudioAlertType =
      typeof audioAlertType === 'string'
        ? audioAlertType
        : DEFAULT_ALERT_TYPE_STRING;
    const resolvedTone = this.resolveLegacyTone(audioAlertTone || DEFAULT_TONE);

    this.notificationConfig = {
      ...this.notificationConfig,
      audioAlertType: serializedAudioAlertType.split('+').filter(Boolean),
      playAlertOnlyWhenHidden: !alwaysPlayAudioAlert,
      alertIfUnreadConversationExist: alertIfUnreadConversationExist,
    };

    this.notificationSoundConfig = {
      ...this.notificationSoundConfig,
      enabled: notificationSoundEnabled,
      repeatNewMessageSoundUntilRead: repeatNewMessageSoundUntilRead,
      preferences: sanitizeNotificationSoundPreferences(
        notificationSoundPreferences
      ),
    };

    this.currentUser = currentUser;

    const previousAudioTone = this.audioConfig.tone;
    this.audioConfig = {
      ...this.audioConfig,
      tone: resolvedTone,
    };

    // Always initialize audio if not exists, or if tone changed
    if (previousAudioTone !== resolvedTone || !this.audioConfig.audio) {
      this.intializeAudio();
    }

    // Preload the sounds this user has enabled (and re-arm the gesture unlock)
    // so they are ready the instant a message arrives.
    this.setupAudioUnlock();
    this.warmUpSounds({ decode: this.audioUnlocked });

    initFaviconSwitcher();
    this.clearRecurringTimer();
    this.playAudioEvery30Seconds();
  };

  shouldPlayAlert = () => {
    if (this.notificationConfig.playAlertOnlyWhenHidden) {
      return !WindowVisibilityHelper.isWindowVisible();
    }
    return true;
  };

  shouldPlayNotificationEvents = () => {
    if (this.notificationSoundConfig.enabled) {
      return true;
    }

    return this.shouldPlayAlert();
  };

  executeRecurringNotification = () => {
    const shouldRepeatWithNotificationSound =
      this.shouldRepeatNewMessageSound();
    const canPlayNow = shouldRepeatWithNotificationSound
      ? true
      : this.shouldPlayAlert();

    if (this.store.hasUnreadConversation() && canPlayNow) {
      if (shouldRepeatWithNotificationSound) {
        this.playNotificationSound(NOTIFICATION_SOUND_EVENTS.NEW_MESSAGE);
      } else {
        this.playAudioAlert();
      }
      showBadgeOnFavicon();
    }
    this.resetRecurringTimer();
  };

  clearRecurringTimer = () => {
    if (this.recurringNotificationTimer) {
      clearTimeout(this.recurringNotificationTimer);
    }
  };

  resetRecurringTimer = () => {
    this.clearRecurringTimer();
    this.recurringNotificationTimer = setTimeout(
      this.executeRecurringNotification,
      NOTIFICATION_TIME
    );
  };

  playAudioEvery30Seconds = () => {
    const { audioAlertType, alertIfUnreadConversationExist } =
      this.notificationConfig;
    const hasLegacyRepeatEnabled =
      !audioAlertType.includes('none') && alertIfUnreadConversationExist;

    // No repeat mode is enabled, dismiss the timer.
    if (!hasLegacyRepeatEnabled && !this.shouldRepeatNewMessageSound()) return;

    this.resetRecurringTimer();
  };

  shouldNotifyOnMessage = message => {
    const { audioAlertType } = this.notificationConfig;
    if (audioAlertType.includes('none')) return false;
    if (audioAlertType.includes('all')) return true;

    const assignedToMe = isConversationAssignedToMe(
      message,
      this.currentUser.id
    );
    const isUnassigned = isConversationUnassigned(message);

    const shouldPlayAudio = [];

    if (
      audioAlertType.includes(EVENT_TYPES.ASSIGNED) ||
      audioAlertType.includes('mine')
    ) {
      shouldPlayAudio.push(assignedToMe);
    }
    if (audioAlertType.includes(EVENT_TYPES.UNASSIGNED)) {
      shouldPlayAudio.push(isUnassigned);
    }
    if (audioAlertType.includes(EVENT_TYPES.NOTME)) {
      shouldPlayAudio.push(!isUnassigned && !assignedToMe);
    }

    return shouldPlayAudio.some(Boolean);
  };

  onNewMessage = message => {
    // If currentUser is not initialized, dismiss the alert
    if (!this.currentUser) {
      return;
    }

    // If the user does not have the permission to view the conversation, then dismiss the alert
    // FIX ME: There shouldn't be a new message if the user has no access to the conversation.
    if (!this.store.hasConversationPermission(this.currentUser)) {
      return;
    }

    // If the conversation status is pending, then dismiss the alert
    // This case is common for all audio event types
    if (this.store.isMessageFromPendingConversation(message)) {
      return;
    }

    // If the message is sent by the current user then dismiss the alert
    if (isMessageFromCurrentUser(message, this.currentUser.id)) {
      return;
    }

    const shouldUseNotificationSound = this.isNotificationSoundEnabled(
      NOTIFICATION_SOUND_EVENTS.NEW_MESSAGE
    );
    if (!shouldUseNotificationSound && !this.shouldNotifyOnMessage(message)) {
      return;
    }

    // If the message type is not incoming or private, then dismiss the alert
    const { message_type: messageType, private: isPrivate } = message;
    if (messageType !== MESSAGE_TYPE.INCOMING && !isPrivate) {
      return;
    }

    if (WindowVisibilityHelper.isWindowVisible()) {
      // If the user looking at the conversation, then dismiss the alert
      if (this.store.isMessageFromCurrentConversation(message)) {
        return;
      }

      // If the user has disabled alerts when active on the dashboard, the dismiss the alert
      if (
        this.notificationConfig.playAlertOnlyWhenHidden &&
        !shouldUseNotificationSound
      ) {
        return;
      }
    }

    const hasPlayedNotificationSound = this.playNotificationSound(
      NOTIFICATION_SOUND_EVENTS.NEW_MESSAGE
    );

    if (!hasPlayedNotificationSound) {
      this.playAudioAlert();
    }

    showBadgeOnFavicon();
    this.playAudioEvery30Seconds();
  };

  onConversationCreated = conversation => {
    if (
      !this.currentUser ||
      !this.store.hasConversationPermission(this.currentUser)
    ) {
      return;
    }

    const performerId = conversation?.performer?.id;
    if (performerId && Number(performerId) === Number(this.currentUser.id)) {
      return;
    }

    if (!this.shouldPlayNotificationEvents()) {
      return;
    }

    const contactId = conversation?.meta?.sender?.id;
    const hasPreviousConversation =
      this.store.hasPreviousConversationForContact(contactId, conversation?.id);
    const visitorEventKey = hasPreviousConversation
      ? NOTIFICATION_SOUND_EVENTS.RETURNING_VISITORS
      : NOTIFICATION_SOUND_EVENTS.NEW_VISITORS;

    const hasPlayedVisitorSound = this.playNotificationSound(visitorEventKey);
    const hasPlayedIncomingChatSound = hasPlayedVisitorSound
      ? false
      : this.playNotificationSound(NOTIFICATION_SOUND_EVENTS.INCOMING_CHATS);

    if (hasPlayedVisitorSound || hasPlayedIncomingChatSound) {
      showBadgeOnFavicon();
    }
  };

  onConversationStatusChange = conversation => {
    if (
      !this.currentUser ||
      !this.store.hasConversationPermission(this.currentUser)
    ) {
      return;
    }

    if (!this.shouldPlayNotificationEvents()) {
      return;
    }

    if (conversation?.status !== 'pending') {
      return;
    }

    if (
      this.playNotificationSound(NOTIFICATION_SOUND_EVENTS.VISITORS_IN_QUEUE)
    ) {
      showBadgeOnFavicon();
    }
  };

  onNotificationCreated = notification => {
    if (
      !this.currentUser ||
      !this.store.hasConversationPermission(this.currentUser)
    ) {
      return;
    }

    if (!this.shouldPlayNotificationEvents()) {
      return;
    }

    const soundEventKey =
      NOTIFICATION_TYPE_TO_SOUND_EVENT[notification?.notification_type];
    if (!soundEventKey) {
      return;
    }

    if (this.playNotificationSound(soundEventKey)) {
      showBadgeOnFavicon();
    }
  };
}

export default new DashboardAudioNotificationHelper(GlobalStore);
